import React from "react";
import { useSearchParams } from "react-router-dom";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminSettings.css";

const formatFileSize = (size) => {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return "0 KB";
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
};

const DEFAULT_POINTS_FORM = {
  pointsPerReal: "1",
  campaignEnabled: false,
  campaignPointsPerReal: "",
  campaignStartAt: "",
  campaignEndAt: "",
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const text = String(value);
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const getPointsFormFromSettings = (settings = {}) => ({
  pointsPerReal:
    settings.pointsPerReal === null || settings.pointsPerReal === undefined
      ? "1"
      : String(settings.pointsPerReal),
  campaignEnabled: Boolean(settings.campaignEnabled),
  campaignPointsPerReal:
    settings.campaignPointsPerReal === null || settings.campaignPointsPerReal === undefined
      ? ""
      : String(settings.campaignPointsPerReal),
  campaignStartAt: toDateInputValue(settings.campaignStartAt),
  campaignEndAt: toDateInputValue(settings.campaignEndAt),
});

const AdminSettings = () => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [banners, setBanners] = React.useState([]);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [activeSubmenu, setActiveSubmenu] = React.useState("banner-inicial");
  const [transitionDurationMs, setTransitionDurationMs] = React.useState("5000");
  const [pointsForm, setPointsForm] = React.useState(DEFAULT_POINTS_FORM);
  const [pointsUpdatedAt, setPointsUpdatedAt] = React.useState("");
  const [pointsLoaded, setPointsLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadingPoints, setLoadingPoints] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [savingPoints, setSavingPoints] = React.useState(false);
  const [deletingBannerId, setDeletingBannerId] = React.useState("");
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const fileInputRef = React.useRef(null);

  const toggleMenu = () => setMenuClose((prev) => !prev);

  React.useEffect(() => {
    const tab = searchParams.get("aba");
    if (tab === "banner-inicial" || tab === "pontos") {
      setActiveSubmenu(tab);
    }
  }, [searchParams]);

  const handleSubmenuChange = (submenu) => {
    setActiveSubmenu(submenu);
    setSearchParams({ aba: submenu });
    setError("");
    setSuccess("");
  };

  const loadBanners = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await Api.get("/public/login-banners");
      setBanners(Array.isArray(response?.data?.items) ? response.data.items : []);
      setTransitionDurationMs(String(response?.data?.transitionDurationMs || 5000));
    } catch (err) {
      console.error("Erro ao carregar banners:", err);
      setError("Nao foi possivel carregar os banners do carrossel.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const loadPointsSettings = React.useCallback(async () => {
    try {
      setLoadingPoints(true);
      setError("");
      const response = await Api.get("/admin/points-settings");
      const settings = response?.data?.settings || {};
      setPointsForm(getPointsFormFromSettings(settings));
      setPointsUpdatedAt(settings.updatedAt || "");
      setPointsLoaded(true);
    } catch (err) {
      console.error("Erro ao carregar configuracoes de pontos:", err);
      setError("Nao foi possivel carregar as configuracoes de pontos.");
    } finally {
      setLoadingPoints(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeSubmenu === "pontos" && !pointsLoaded) {
      loadPointsSettings();
    }
  }, [activeSubmenu, loadPointsSettings, pointsLoaded]);

  const handleSelectFiles = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setError("");
    setSuccess("");
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (selectedFiles.length === 0) {
      setError("Selecione ao menos uma imagem para enviar.");
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("images", file));

    try {
      setUploading(true);
      setError("");
      setSuccess("");

      const response = await Api.post("/admin/login-banners", formData, {
        timeout: 30000,
      });
      setBanners(Array.isArray(response?.data?.items) ? response.data.items : []);
      setTransitionDurationMs(String(response?.data?.transitionDurationMs || transitionDurationMs));
      setSelectedFiles([]);
      setSuccess("Banners enviados com sucesso. O carrossel ja foi atualizado.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Erro ao enviar banners:", err);
      setError(
        err?.code === "ECONNABORTED"
          ? "O envio demorou mais do que o esperado. Tente novamente."
          : err?.response?.data?.message || "Nao foi possivel enviar os banners."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();

    const duration = Number(transitionDurationMs);

    if (!Number.isFinite(duration)) {
      setError("Informe um tempo de transicao valido em milissegundos.");
      setSuccess("");
      return;
    }

    try {
      setSavingSettings(true);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/login-banners/settings", {
        transitionDurationMs: duration,
      });

      setTransitionDurationMs(String(response?.data?.transitionDurationMs || duration));
      setSuccess("Tempo de transicao atualizado com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar configuracoes do banner inicial:", err);
      setError(
        err?.response?.data?.message ||
          "Nao foi possivel salvar o tempo de transicao do banner inicial."
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePointsChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;

    setPointsForm((current) => ({
      ...current,
      [field]: value,
    }));
    setError("");
    setSuccess("");
  };

  const handleSavePointsSettings = async (event) => {
    event.preventDefault();

    try {
      setSavingPoints(true);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/points-settings", {
        pointsPerReal: pointsForm.pointsPerReal,
        campaignEnabled: pointsForm.campaignEnabled,
        campaignPointsPerReal: pointsForm.campaignPointsPerReal,
        campaignStartAt: pointsForm.campaignStartAt,
        campaignEndAt: pointsForm.campaignEndAt,
      });

      const settings = response?.data?.settings || {};
      setPointsForm(getPointsFormFromSettings(settings));
      setPointsUpdatedAt(settings.updatedAt || "");
      setSuccess("Configuracoes de pontos salvas com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar configuracoes de pontos:", err);
      setError(err?.response?.data?.message || "Nao foi possivel salvar os pontos.");
    } finally {
      setSavingPoints(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm("Deseja remover este banner do carrossel?")) return;

    try {
      setDeletingBannerId(bannerId);
      setError("");
      setSuccess("");

      await Api.delete(`/admin/login-banners/${bannerId}`);
      setBanners((current) => current.filter((item) => item.id !== bannerId));
      setSuccess("Banner removido com sucesso.");
    } catch (err) {
      console.error("Erro ao remover banner:", err);
      setError(err?.response?.data?.message || "Nao foi possivel remover o banner.");
    } finally {
      setDeletingBannerId("");
    }
  };

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} settingsActive="active" />

      <main className="main-content">
        <Topbar
          page={activeSubmenu === "pontos" ? "Configuracoes / Pontos" : "Configuracoes / Banner inicial"}
          onToggleMenu={toggleMenu}
        />

        <section className="admin-settings-grid">
          <article className="admin-settings-panel">
            <div className="admin-settings-header">
              <div>
                <h2>Submenu das configuracoes</h2>
                <p>Organize os ajustes do super administrador por area do sistema.</p>
              </div>
              <span className="admin-settings-badge">Super administrador</span>
            </div>

            <div className="admin-settings-submenu">
              <button
                type="button"
                className={activeSubmenu === "banner-inicial" ? "is-active" : ""}
                onClick={() => handleSubmenuChange("banner-inicial")}
              >
                Banner inicial
              </button>
              <button
                type="button"
                className={activeSubmenu === "pontos" ? "is-active" : ""}
                onClick={() => handleSubmenuChange("pontos")}
              >
                Pontos
              </button>
            </div>
          </article>

          {activeSubmenu === "banner-inicial" ? (
            <>
              <article className="admin-settings-panel">
                <div className="admin-settings-header">
                  <div>
                    <h2>Configuracoes do banner inicial</h2>
                    <p>
                      Todas as opcoes abaixo controlam o carrossel exibido na tela inicial do
                      sistema.
                    </p>
                  </div>
                </div>

                <div className="admin-settings-split">
                  <form className="admin-settings-card" onSubmit={handleUpload}>
                    <div className="admin-settings-card-header">
                      <h3>Imagens do carrossel</h3>
                      <p>Envie os slides na ordem em que eles devem aparecer no login.</p>
                    </div>

                    <label className="admin-upload-box" htmlFor="login-banners">
                      <span>Selecionar imagens</span>
                      <small>Formatos aceitos: JPG, PNG, WEBP. Limite de 10 MB por arquivo.</small>
                    </label>

                    <input
                      ref={fileInputRef}
                      id="login-banners"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleSelectFiles}
                    />

                    {selectedFiles.length > 0 ? (
                      <div className="admin-selected-files">
                        {selectedFiles.map((file) => (
                          <div
                            className="admin-selected-file"
                            key={`${file.name}-${file.lastModified}`}
                          >
                            <strong>{file.name}</strong>
                            <span>{formatFileSize(file.size)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="admin-settings-help">
                        A ordem do carrossel segue a ordem de envio. Para reorganizar, remova e
                        envie novamente na sequencia desejada.
                      </p>
                    )}

                    <div className="admin-settings-actions">
                      <button type="submit" disabled={uploading || selectedFiles.length === 0}>
                        {uploading ? "Enviando..." : "Enviar banners"}
                      </button>
                    </div>
                  </form>

                  <form className="admin-settings-card" onSubmit={handleSaveSettings}>
                    <div className="admin-settings-card-header">
                      <h3>Tempo de transicao</h3>
                      <p>Defina em milissegundos quanto tempo cada slide fica visivel.</p>
                    </div>

                    <label className="admin-settings-field" htmlFor="transition-duration-ms">
                      <span>Duracao do slide em milissegundos</span>
                      <input
                        id="transition-duration-ms"
                        type="number"
                        min="500"
                        step="100"
                        value={transitionDurationMs}
                        onChange={(event) => setTransitionDurationMs(event.target.value)}
                        placeholder="5000"
                      />
                      <small>Valor minimo de 500 ms e maximo de 60000 ms.</small>
                    </label>

                    <div className="admin-settings-actions">
                      <button type="submit" disabled={savingSettings}>
                        {savingSettings ? "Salvando..." : "Salvar tempo de transicao"}
                      </button>
                    </div>
                  </form>
                </div>

                {error ? <div className="admin-settings-message error">{error}</div> : null}
                {success ? <div className="admin-settings-message success">{success}</div> : null}
              </article>

              <article className="admin-settings-panel">
                <div className="admin-settings-header compact">
                  <div>
                    <h2>Banners cadastrados</h2>
                    <p>
                      {loading
                        ? "Carregando imagens..."
                        : `${banners.length} banner(s) ativo(s) no banner inicial`}
                    </p>
                  </div>
                </div>

                {loading ? (
                  <div className="admin-settings-empty">Carregando banners...</div>
                ) : banners.length === 0 ? (
                  <div className="admin-settings-empty">
                    Nenhum banner enviado ainda. O login continua usando as imagens padrao.
                  </div>
                ) : (
                  <div className="admin-banner-grid">
                    {banners.map((banner, index) => (
                      <article className="admin-banner-card" key={banner.id}>
                        <div className="admin-banner-preview">
                          <img src={banner.url} alt={banner.originalName || `Banner ${index + 1}`} />
                        </div>

                        <div className="admin-banner-body">
                          <strong>{banner.originalName || `Banner ${index + 1}`}</strong>
                          <span>Posicao {index + 1}</span>
                          <span>{formatFileSize(banner.size)}</span>
                        </div>

                        <button
                          type="button"
                          className="admin-banner-remove"
                          onClick={() => handleDelete(banner.id)}
                          disabled={Boolean(deletingBannerId)}
                        >
                          {deletingBannerId === banner.id ? "Removendo..." : "Remover"}
                        </button>
                      </article>
                    ))}
                  </div>
                )}
              </article>
            </>
          ) : null}

          {activeSubmenu === "pontos" ? (
            <article className="admin-settings-panel">
              <div className="admin-settings-header">
                <div>
                  <h2>Sistema de pontos</h2>
                  <p>
                    Defina a regra padrao de pontos por real e uma campanha temporaria com
                    pontuacao especial.
                  </p>
                </div>
                <span className="admin-settings-badge">Super administrador</span>
              </div>

              {loadingPoints ? (
                <div className="admin-settings-empty">Carregando configuracoes de pontos...</div>
              ) : (
                <form className="admin-settings-points-form" onSubmit={handleSavePointsSettings}>
                  <div className="admin-settings-split">
                    <section className="admin-settings-card">
                      <div className="admin-settings-card-header">
                        <h3>Regra padrao</h3>
                        <p>Exemplo: com 1 ponto por real, R$ 100,50 gera 100 pontos.</p>
                      </div>

                      <label className="admin-settings-field" htmlFor="points-per-real">
                        <span>Pontos para cada R$ 1,00</span>
                        <input
                          id="points-per-real"
                          type="number"
                          min="0"
                          step="0.01"
                          value={pointsForm.pointsPerReal}
                          onChange={handlePointsChange("pointsPerReal")}
                          placeholder="1"
                        />
                        <small>O sistema sempre arredonda os pontos para baixo.</small>
                      </label>
                    </section>

                    <section className="admin-settings-card">
                      <div className="admin-settings-card-header">
                        <h3>Campanha de pontos</h3>
                        <p>Use para datas especiais, como R$ 1,00 valendo 2 pontos.</p>
                      </div>

                      <label className="admin-settings-checkbox">
                        <input
                          type="checkbox"
                          checked={pointsForm.campaignEnabled}
                          onChange={handlePointsChange("campaignEnabled")}
                        />
                        <span>Ativar campanha</span>
                      </label>

                      <label className="admin-settings-field" htmlFor="campaign-points-per-real">
                        <span>Pontos da campanha para cada R$ 1,00</span>
                        <input
                          id="campaign-points-per-real"
                          type="number"
                          min="0"
                          step="0.01"
                          value={pointsForm.campaignPointsPerReal}
                          onChange={handlePointsChange("campaignPointsPerReal")}
                          placeholder="2"
                          disabled={!pointsForm.campaignEnabled}
                        />
                      </label>

                      <div className="admin-settings-date-grid">
                        <label className="admin-settings-field" htmlFor="campaign-start-at">
                          <span>Inicio</span>
                          <input
                            id="campaign-start-at"
                            type="date"
                            value={pointsForm.campaignStartAt}
                            onChange={handlePointsChange("campaignStartAt")}
                            disabled={!pointsForm.campaignEnabled}
                          />
                        </label>

                        <label className="admin-settings-field" htmlFor="campaign-end-at">
                          <span>Fim</span>
                          <input
                            id="campaign-end-at"
                            type="date"
                            value={pointsForm.campaignEndAt}
                            onChange={handlePointsChange("campaignEndAt")}
                            disabled={!pointsForm.campaignEnabled}
                          />
                        </label>
                      </div>
                    </section>
                  </div>

                  <div className="admin-settings-points-summary">
                    <strong>Como sera calculado</strong>
                    <span>
                      R$ 100,50 em comissao =
                      {" "}
                      {Math.floor(100.5 * Number(pointsForm.pointsPerReal || 0))}
                      {" "}
                      pontos na regra padrao.
                    </span>
                    {pointsForm.campaignEnabled ? (
                      <span>
                        Na campanha =
                        {" "}
                        {Math.floor(100.5 * Number(pointsForm.campaignPointsPerReal || 0))}
                        {" "}
                        pontos.
                      </span>
                    ) : null}
                    {pointsUpdatedAt ? <small>Ultima atualizacao: {String(pointsUpdatedAt)}</small> : null}
                  </div>

                  <div className="admin-settings-actions">
                    <button type="submit" disabled={savingPoints}>
                      {savingPoints ? "Salvando..." : "Salvar configuracoes de pontos"}
                    </button>
                  </div>

                  {error ? <div className="admin-settings-message error">{error}</div> : null}
                  {success ? <div className="admin-settings-message success">{success}</div> : null}
                </form>
              )}
            </article>
          ) : null}
        </section>
      </main>
    </div>
  );
};

export default AdminSettings;
